import type { Competency, Difficulty } from '@/types/interview'
import type { SubscriptionTier } from '@/types/user'
import posthog from 'posthog-js'
import { useMemo } from 'react'

// Memoized so consumers can safely put the returned object (or a callback
// derived from it) in a useEffect/useCallback dependency array without an
// unstable reference re-triggering on every render.
export function useAnalytics() {
  return useMemo(
    () => ({
      trackUserSignedUp: (props: { method: 'email' | 'google'; trigger: 'landing' | 'gate' }) => {
        posthog.capture('user_signed_up', props)
      },

      trackInterviewStarted: (props: {
        session_id: string
        role: string
        competency: Competency
        difficulty: Difficulty
        subscription_tier: SubscriptionTier
      }) => {
        posthog.capture('interview_started', props)
      },

      trackInterviewQuestionAnswered: (props: {
        session_id: string
        question_sequence: number
        is_follow_up: boolean
        word_count: number
        competency: Competency
      }) => {
        posthog.capture('interview_question_answered', props)
      },

      trackInterviewCompleted: (props: {
        session_id: string
        overall_score: number
        competency: Competency
        difficulty: Difficulty
        word_count_total: number
        subscription_tier: SubscriptionTier
      }) => {
        posthog.capture('interview_completed', props)
      },

      trackSubscriptionUpgraded: (props: {
        previous_tier: SubscriptionTier
        plan: string
      }) => {
        posthog.capture('subscription_upgraded', props)
      },

      trackProgressSectionViewed: (props: {
        completed_sessions: number
        has_star_data: boolean
      }) => {
        posthog.capture('progress_section_viewed', props)
      },

      trackPracticeNextClicked: (props: {
        cta_type: 'star_component' | 'competency'
        target: string
        role: string
      }) => {
        posthog.capture('practice_next_clicked', props)
      },

      trackRewriteGenerated: (props: {
        session_id: string
        competency: string
        word_count: number
      }) => {
        posthog.capture('rewrite_generated', props)
      },

      trackSpeechToTextUsed: (props: {
        session_id: string
        question_sequence: number
      }) => {
        posthog.capture('speech_to_text_used', props)
      },

      trackStorySaved: (props: { competency: string; difficulty: string; source: string }) => {
        posthog.capture('story_saved', props)
      },

      trackStoryDeleted: (props: { competency: string }) => {
        posthog.capture('story_deleted', props)
      },

      trackStoryBankViewed: (props: { story_count: number; is_pro: boolean }) => {
        posthog.capture('story_bank_viewed', props)
      },

      trackStoryViewed: (props: { competency: string; source: string }) => {
        posthog.capture('story_viewed', props)
      },

      trackStoryUpdated: (props: { fields: string[] }) => {
        posthog.capture('story_updated', props)
      },

      trackStoryFavorited: (props: { favorited: boolean }) => {
        posthog.capture('story_favorited', props)
      },

      trackStoryCapHit: (props: { story_count: number }) => {
        posthog.capture('story_cap_hit', props)
      },

      trackStoryBankSearched: (props: { result_count: number }) => {
        posthog.capture('story_bank_searched', props)
      },

      trackIntakeStarted: (props: { trigger: string; has_resume: boolean }) => {
        posthog.capture('intake_started', props)
      },

      trackIntakeStoryDrafted: (props: {
        competency: string
        missing_components_count: number
      }) => {
        posthog.capture('intake_story_drafted', props)
      },

      trackIntakeStorySaved: (props: {
        competency: string
        story_count: number
        was_edited: boolean
      }) => {
        posthog.capture('intake_story_saved', props)
      },

      trackIntakeCapHit: (props: { story_count: number }) => {
        posthog.capture('intake_cap_hit', props)
      },

      trackIntakeCompleted: (props: { stories_created: number; turns_used: number }) => {
        posthog.capture('intake_completed', props)
      },

      trackIntakeAbandoned: (props: { stories_created: number; turns_used: number }) => {
        posthog.capture('intake_abandoned', props)
      },
    }),
    []
  )
}
